import { z } from "zod";
import Modal from "../../../../components/Modal";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import FormInput from "../../../../components/FormInput";
import Button from "../../../../components/Button";
import { useMutation } from "@apollo/client";
import {
  Contact,
  ContactsDocument,
  CreateContactDocument,
  UpdateContactDocument,
} from "../../../../__generated__/graphql";
import toast from "react-hot-toast";
import { useEffect } from "react";

const createContactSchema = z.object({
  name: z.string().min(1, "Un nom est requis"),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  description: z.string().optional(),
});

type CreateContactFormData = z.infer<typeof createContactSchema>;

export default function CreateContactModal({
  eventId,
  projectId,
  isOpen,
  onClose,
  contact,
}: {
  eventId?: string;
  projectId?: string;
  isOpen: boolean;
  onClose: () => void;
  contact?: Contact;
}) {
  const [createContact, { loading: isCreatingContact }] = useMutation(
    CreateContactDocument,
    {
      onCompleted: () => {
        toast.success("Contact créé avec succès");
        handleClose();
      },
      onError: () => {
        toast.error("Une erreur est survenue lors de la création du contact");
      },
    }
  );
  const [updateContact, { loading: isUpdatingContact }] = useMutation(
    UpdateContactDocument,
    {
      onCompleted: () => {
        toast.success("Contact modifié avec succès");
        handleClose();
      },
      onError: () => {
        toast.error(
          "Une erreur est survenue lors de la modification du contact"
        );
      },
    }
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<CreateContactFormData>({
    resolver: zodResolver(createContactSchema),
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        name: contact?.name ?? "",
        email: contact?.email ?? "",
        phoneNumber: contact?.phoneNumber ?? "",
        description: contact?.description ?? "",
      });
    }
  }, [contact, isOpen, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: CreateContactFormData) => {
    if (contact) {
      updateContact({
        variables: {
          updateContactInput: {
            id: contact.id,
            name: data.name,
            email: data.email,
            phoneNumber: data.phoneNumber,
            description: data.description,
            eventId,
            projectId,
          },
        },
        refetchQueries: [ContactsDocument],
      });
    } else {
      createContact({
        variables: {
          createContactInput: {
            name: data.name,
            email: data.email,
            phoneNumber: data.phoneNumber,
            description: data.description,
            eventId,
            projectId,
          },
        },
        refetchQueries: [ContactsDocument],
      });
    }
  };

  return (
    <Modal
      isModalOpen={isOpen}
      onClose={handleClose}
      title="Créer un contact"
      confirmClose={isDirty}
      className="min-w-md"
      dataTestId="contact-modal"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FormInput
          id="name"
          label="Nom"
          placeholder="Nom"
          error={errors.name}
          register={register}
          dataTestId="contact-name-input"
        />
        <FormInput
          id="email"
          label="Email"
          type="email"
          placeholder="Email"
          isOptional={true}
          error={errors.email}
          register={register}
        />
        <FormInput
          id="phoneNumber"
          type="tel"
          label="Numéro de téléphone"
          placeholder="Numéro de téléphone"
          error={errors.phoneNumber}
          isOptional={true}
          register={register}
        />
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea
            {...register("description")}
            rows={2}
            className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
          />
        </div>
        <Button
          type="submit"
          className="w-full"
          isLoading={isCreatingContact || isUpdatingContact}
          dataTestId="contact-submit-button"
        >
          {contact ? "Modifier" : "Créer"}
        </Button>
      </form>
    </Modal>
  );
}
